import { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import {
  getNotifications,
  markAllNotificationsAsRead,
} from '../services/notificationService';

const PAGE_SIZE = 20;

const typeIcons = {
  match: `${process.env.PUBLIC_URL}/images/new-match.svg`,
  user: `${process.env.PUBLIC_URL}/images/notif-user.svg`,
  report: `${process.env.PUBLIC_URL}/images/notif-report.svg`,
  verified: `${process.env.PUBLIC_URL}/images/notif-verified.svg`,
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function normalizeType(type) {
  const value = String(type || '').toLowerCase();

  if (value.includes('match')) return 'match';
  if (value.includes('report')) return 'report';
  if (value.includes('verif')) return 'verified';
  if (
    value === 'user_registered' ||
    value.includes('register') ||
    value.includes('signup') ||
    value.includes('user')
  ) {
    return 'user';
  }
  return 'user';
}

function mapNotification(item) {
  const badge = typeof item.status === 'string' && item.status.trim()
    ? item.status
    : null;

  return {
    id: item.id,
    title: item.title || 'Notification',
    message: item.description || '',
    time: formatRelativeTime(item.createdAt),
    type: normalizeType(item.type),
    read: Boolean(item.isRead),
    badge,
  };
}

export default function Notifications() {
  const { unreadCount, refreshUnreadCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [readAllLoading, setReadAllLoading] = useState(false);
  const [readAllError, setReadAllError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const prevFilterRef = useRef(filter);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    const filterChanged = prevFilterRef.current !== filter;
    prevFilterRef.current = filter;

    if (filterChanged && page !== 1) {
      setPage(1);
      setNotifications([]);
      setHasMore(true);
      return undefined;
    }

    let cancelled = false;
    const append = !filterChanged && page > 1;

    async function loadNotifications() {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (filterChanged) {
          setNotifications([]);
          setHasMore(true);
        }
      }
      setError('');

      try {
        const response = await getNotifications({
          page,
          limit: PAGE_SIZE,
          readStatus: filter,
        });

        if (cancelled) return;

        const data = Array.isArray(response.data) ? response.data.map(mapNotification) : [];
        const totalPages = response.pagination?.totalPages || 1;

        setNotifications((prevList) => (append ? [...prevList, ...data] : data));
        setHasMore(page < totalPages);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch notifications.');
        if (!append) {
          setNotifications([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [page, filter, reloadToken]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          setPage((prev) => prev + 1);
        }
      },
      { root, rootMargin: '240px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, notifications.length]);

  function handleFilterChange(nextFilter) {
    if (nextFilter === filter) return;
    setFilter(nextFilter);
    setPage(1);
    setNotifications([]);
    setHasMore(true);
  }

  async function handleReadAll() {
    if (readAllLoading || unreadCount === 0) return;

    setReadAllLoading(true);
    setReadAllError('');
    try {
      await markAllNotificationsAsRead();
      await refreshUnreadCount();

      if (filter === 'unread') {
        setNotifications([]);
        setHasMore(false);
        setPage(1);
      } else {
        setPage(1);
        setNotifications([]);
        setHasMore(true);
        setReloadToken((token) => token + 1);
      }
    } catch (err) {
      setReadAllError(err.message || 'Failed to mark all notifications as read.');
    } finally {
      setReadAllLoading(false);
    }
  }

  return (
    <div className="notifications-page main-bg-top">
      <div className="notif-toolbar">
        <div className="notif-tabs">
          <button
            className={`notif-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            All
          </button>
          <button
            className={`notif-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => handleFilterChange('unread')}
          >
            Unread {unreadCount > 0 && <span className="notif-tab-count">{unreadCount}</span>}
          </button>
        </div>

        <button
          type="button"
          className="notif-read-all-btn"
          onClick={handleReadAll}
          disabled={readAllLoading || unreadCount === 0}
        >
          {readAllLoading ? 'Marking...' : 'Read All'}
        </button>
      </div>

      {(error || readAllError) && (
        <div className="restaurants-error" style={{ margin: '16px 20px 0' }}>
          {error || readAllError}
        </div>
      )}

      <div className="notif-list" ref={listRef}>
        {loading && notifications.length === 0 ? (
          <div className="notifications-empty">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">No notifications found.</div>
        ) : (
          <>
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-item ${!n.read ? 'unread' : ''}`}
              >
                <div className="notif-icon">
                  <img src={typeIcons[n.type] || typeIcons.user} alt={n.type} />
                </div>
                <div className="notif-body">
                  <div className="notif-title">{n.title}</div>
                  <div className="notif-message">{n.message}</div>
                </div>
                <div className="notif-right">
                  <span className="notif-time">{n.time}</span>
                  {n.badge && (
                    <span className={`status-badge status-${String(n.badge).toLowerCase()}`}>
                      {n.badge}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <div ref={sentinelRef} aria-hidden="true" />

            {loadingMore && (
              <div className="restaurants-loading-more">Loading more...</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
